import { useCallback, useEffect, useState } from 'react';
import { useMedplum, useSubscription } from '@medplum/react';
import { Encounter, Patient } from '@medplum/fhirtypes';

export const useCurrentEncounter = (patient?: Patient) => {
  const medplum = useMedplum();
  const [currentEncounter, setCurrentEncounter] = useState<
    Encounter | undefined
  >(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadCurrentEncounter = useCallback(async () => {
    if (!patient?.id) {
      setCurrentEncounter(undefined);
      return;
    }

    setLoading(true);
    try {
      const result = await medplum.search(
        'Encounter',
        `patient=${patient.id}&status=in-progress&_sort=-date&_count=1`
      );
      const encounter = result.entry?.[0]?.resource as Encounter | undefined;
      setCurrentEncounter(encounter ?? undefined);
    } catch (error) {
      console.error('Error loading encounter:', error);
    } finally {
      setLoading(false);
    }
  }, [medplum, patient]);

  useEffect(() => {
    loadCurrentEncounter();
  }, [loadCurrentEncounter]);

  useSubscription(
    patient?.id ? `Encounter?patient=Patient/${patient.id}` : '',
    (bundle) => {
      // Find the encounter in the bundle
      const encounter = bundle.entry?.find(
        (e) => e.resource?.resourceType === 'Encounter'
      )?.resource as Encounter | undefined;

      if (!encounter) {
        return;
      }

      // Update currentEncounter if it is in-progress
      if (encounter.status === 'in-progress') {
        setCurrentEncounter(encounter);
      } else if (currentEncounter?.id === encounter.id) {
        // If current encounter finished/cancelled, clear it
        setCurrentEncounter(undefined);
      }

      // Always increment refreshKey for SearchControl components
      setRefreshKey((prev) => prev + 1);
    }
  );

  return {
    currentEncounter,
    loading,
    setCurrentEncounter,
    reload: loadCurrentEncounter,
    refreshKey, // For SearchControl components
  };
};
